import { Input, Button, Icon } from '@data-products-and-ai/react-components';
import { FC, useState } from 'react';
import SparePartsResultsCard from './SparePartsResultsCard';
import useToken from '../../utils/useToken';
import {
  sparePartsApi,
  SparePartsApiError,
  type Part,
} from '../../utils/sparePartsApi';
import './SparePartsSelector.scss';

interface SparePartsSelectorProps {
  onSearch?: (value: string) => void;
}

const SparePartsSelector: FC<SparePartsSelectorProps> = ({
  onSearch,
}: SparePartsSelectorProps) => {
  const getToken = useToken();

  const [inputValue, setInputValue] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<Part[]>([]);
  const [serialNumber, setSerialNumber] = useState<string>('');

  const [openSections, setOpenSections] = useState<{ [key: number]: boolean }>(
    {},
  );

  /**
   * Group parts by header rows
   */
  const groupPartsByHeader = (parts: Part[]) => {
    const groups: { title: string; parts: Part[] }[] = [];
    let currentGroup: { title: string; parts: Part[] } | null = null;

    parts.forEach((part) => {
      if (part.is_header) {
        currentGroup = {
          title: part.Description || part.partName || 'Spare Parts',
          parts: [],
        };
        groups.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.parts.push(part);
      }
    });

    return groups;
  };

  const handleToggleSection = (index: number) => {
    setOpenSections((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSearch = async () => {
    if (!inputValue.trim()) {
      setError('Please enter a turbine serial number');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();

      if (!token) {
        setError('Please sign in to continue.');
        setSearchPerformed(false);
        return;
      }

      const response = await sparePartsApi.fetchSparePartsBySerial(
        inputValue.trim(),
        token.accessToken,
      );

      setSearchResults(response.parts);
      setSerialNumber(response.serial);
      setSearchPerformed(true);
      setOpenSections({});

      if (onSearch) {
        onSearch(inputValue);
      }
    } catch (err) {
      if (err instanceof SparePartsApiError && err.statusCode === 404) {
        setSearchResults([]);
        setSerialNumber(inputValue.trim());
        setSearchPerformed(true);
        setError(null);
      } else {
        const errorMessage =
          err instanceof SparePartsApiError
            ? err.message
            : 'Failed to fetch spare parts. Please try again.';

        setError(errorMessage);
        setSearchPerformed(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      setIsLoading(true);

      const token = await getToken();

      if (!token) {
        setError('Please sign in to continue.');
        return;
      }

      const blob = await sparePartsApi.downloadSparePartsExcel(
        serialNumber,
        token.accessToken,
      );

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `spare_parts_${serialNumber}.xlsx`);
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage =
        err instanceof SparePartsApiError
          ? err.message
          : 'Failed to download spare parts. Please try again.';

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const groupedParts = groupPartsByHeader(searchResults);

  return (
    <div className="sparePartsSelector-wrapper">
      <div
        className="sparePartsSelector-container"
        data-testid="spare-parts-selector"
      >
        <div className="sparePartsSelector-content">
          <div className="sparePartsSelector-title">Search for Spare Parts</div>

          <div className="sparePartsSelector-subtitle">
            Enter Turbine Serial Number
          </div>

          <div className="sparePartsSelector-searchGroup">
            <div className="sparePartsSelector-inputWrapper">
              <Input
                id="turbineSerialNumber"
                width="100%"
                onChange={(event) => setInputValue(event.target.value)}
                onKeyPress={(event) => {
                  if (event.key === 'Enter' && !isLoading) {
                    handleSearch();
                  }
                }}
                placeholder="e.g, 73205, T123, BC456"
                disabled={isLoading}
              />
            </div>

            <button
              className="sparePartsSelector-button"
              onClick={handleSearch}
              disabled={isLoading}
            >
              {isLoading ? 'Loading...' : 'Get Spare Parts'}
            </button>
          </div>
          {error && (
            <div
              className="sparePartsSelector-error"
              style={{
                color: '#d32f2f',
                marginTop: '12px',
                padding: '8px',
                backgroundColor: '#ffebee',
                borderRadius: '4px',
              }}
            >
              {error}
            </div>
          )}

          <div className="sparePartsSelector-outline"></div>

          {searchPerformed && !isLoading && searchResults.length > 0 && (
            <div className="sparePartsSelector-downloadAction">
              <Button
                allCaps={false}
                size="small"
                type="primaryOutline"
                icon={
                  <Icon icon="IconDownload" iconSize={0.7} color="#8a00e5" />
                }
                onClick={handleDownloadCSV}
                disabled={isLoading}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </div>

      {searchPerformed && groupedParts.length > 0 && (
        <div className="sparePartsSelector-results">
          {groupedParts.map((group, index) => {
            const isOpen = openSections[index];

          return (
            <div key={index} className="sparePartsSelector-section">
              <div
                className="sparePartsSelector-sectionTitle"
                onClick={() => handleToggleSection(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleToggleSection(index);
                  }
                }}
              >
                <span>{group.title}</span>
                <Icon
                  icon={isOpen ? 'IconCaretUp' : 'IconCaretDown'}
                  iconSize={0.8}
                  color="#8a00e5"
                />
              </div>

              {isOpen &&
                (group.parts.length > 0 ? (
                  <SparePartsResultsCard title="" parts={group.parts} />
                ) : (
                  <div className="sparePartsSelector-emptySection">
                    No parts in this section
                  </div>
                ))}
            </div>
          );
          })}
        </div>
      )}

      {searchPerformed &&
        searchResults.length === 0 &&
        !isLoading &&
        !error && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            No spare parts found for this serial number.
          </div>
        )}
    </div>
  );
};

export default SparePartsSelector;
